/**
 * A job the server reports as still 'pending' (never claimed by a worker)
 * well past its own eta_seconds estimate means no worker is claiming
 * anything -- not "the queue is just busy". Shared by createJobRunner and
 * web's preWarmEigensolve, both of which poll a job's status in a loop
 * with no other overall wall-clock cap: without this, a job that's never
 * claimed (worker down, worker crashed, queue misconfigured) polls
 * successfully forever -- an open-ended hang whose only symptom is a
 * spinner that never resolves.
 *
 * STUCK_PENDING_ETA_MULTIPLIER (3x the server's own estimate, itself a
 * rough flat per-job-ahead rate -- see backend's _ETA_PER_JOB_SEC) gives
 * generous slack for a genuinely busy queue or a slow-starting worker.
 * MIN_STUCK_PENDING_MS covers eta_seconds being absent/zero (e.g.
 * queue_position 0 reports no eta) without firing on every job's first
 * poll.
 */
export const STUCK_PENDING_ETA_MULTIPLIER = 3;
export const MIN_STUCK_PENDING_MS = 60_000;

export const STUCK_PENDING_MESSAGE =
  'This solve has been queued but never started -- the compute service may be ' +
  'temporarily unavailable. Please try again shortly.';

export interface PendingStalenessTracker {
  /**
   * Call on every poll that returns status 'pending'. Returns true once
   * the job has been pending, with no resume, for longer than its own ETA
   * justifies.
   */
  observePending(etaSeconds: number | undefined, resumeCount: number | undefined, nowMs: number): boolean;
  /**
   * Call on every poll that does NOT return 'pending' (running/done/error)
   * -- clears the tracked pending-run so a later pending status (should
   * the job somehow revert) starts a fresh clock instead of inheriting a
   * stale one.
   */
  reset(): void;
}

/**
 * Tracks the current unbroken run of 'pending' polls for one job. A
 * server-side resume (worker crash mid-solve resets the job to pending
 * again -- see poll_optimize's resume_count field) also starts a fresh
 * clock: a resumed job deserves its own ETA window, not credit against
 * time already spent on the attempt that crashed.
 */
export function createPendingStalenessTracker(): PendingStalenessTracker {
  let pendingSinceMs: number | null = null;
  let lastResumeCount = 0;

  return {
    observePending(etaSeconds, resumeCount, nowMs) {
      const resolvedResumeCount = resumeCount ?? 0;
      if (pendingSinceMs === null || resolvedResumeCount !== lastResumeCount) {
        pendingSinceMs = nowMs;
        lastResumeCount = resolvedResumeCount;
      }
      const etaMs = (etaSeconds ?? 0) * 1000;
      const thresholdMs = Math.max(etaMs * STUCK_PENDING_ETA_MULTIPLIER, MIN_STUCK_PENDING_MS);
      return nowMs - pendingSinceMs >= thresholdMs;
    },
    reset() {
      pendingSinceMs = null;
    },
  };
}
